'use client';

import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInlineEdit } from './InlineEditContext';

function getCaretRangeFromPoint(doc, x, y) {
    const position = doc.caretPositionFromPoint?.(x, y);
    if (position) {
        const range = doc.createRange();
        range.setStart(position.offsetNode, position.offset);
        range.collapse(true);
        return range;
    }

    return doc.caretRangeFromPoint?.(x, y) || null;
}

function getTextOffsetFromPoint(root, x, y) {
    const doc = root?.ownerDocument;
    if (!doc) return null;

    const pointRange = getCaretRangeFromPoint(doc, x, y);
    if (!pointRange || !root.contains(pointRange.startContainer)) return null;

    const offsetRange = doc.createRange();
    offsetRange.selectNodeContents(root);
    offsetRange.setEnd(pointRange.startContainer, pointRange.startOffset);
    return offsetRange.toString().length;
}

function getRangeAtTextOffset(root, textOffset) {
    const doc = root.ownerDocument;
    const nodeFilter = doc.defaultView?.NodeFilter;
    const range = doc.createRange();

    if (!nodeFilter || textOffset == null) {
        range.selectNodeContents(root);
        range.collapse(false);
        return range;
    }

    const walker = doc.createTreeWalker(root, nodeFilter.SHOW_TEXT);
    let remaining = Math.max(0, textOffset);
    let lastTextNode = null;
    let textNode = walker.nextNode();

    while (textNode) {
        lastTextNode = textNode;
        const length = textNode.textContent?.length || 0;
        if (remaining <= length) {
            range.setStart(textNode, remaining);
            range.collapse(true);
            return range;
        }
        remaining -= length;
        textNode = walker.nextNode();
    }

    if (lastTextNode) {
        range.setStart(lastTextNode, lastTextNode.textContent?.length || 0);
        range.collapse(true);
    } else {
        range.selectNodeContents(root);
        range.collapse(false);
    }
    return range;
}

export function InlineEditableText({
    children,
    className,
    fieldKey: explicitFieldKey,
}) {
    const inlineEdit = useInlineEdit();
    const wrapperRef = useRef(null);
    const contentRef = useRef(null);
    const caretOffsetRef = useRef(null);
    const fieldKeyRef = useRef(null);
    const isTextValue =
        typeof children === 'string' || typeof children === 'number';
    const rawValue = isTextValue ? String(children) : '';
    const draftRef = useRef(rawValue);
    const resolvedFieldKeyFromValue = useMemo(
        () =>
            !explicitFieldKey && rawValue && inlineEdit?.resolveTextFieldKey
                ? inlineEdit.resolveTextFieldKey(rawValue)
                : null,
        [explicitFieldKey, inlineEdit, rawValue]
    );
    if (resolvedFieldKeyFromValue) {
        fieldKeyRef.current = resolvedFieldKeyFromValue;
    }
    const resolvedFieldKey = isTextValue
        ? explicitFieldKey || resolvedFieldKeyFromValue || fieldKeyRef.current
        : null;
    const canInlineEdit = Boolean(
        (inlineEdit?.enabled ||
            inlineEdit?.enabledFieldKeys?.includes(resolvedFieldKey)) &&
            resolvedFieldKey &&
            typeof inlineEdit.onInlineSave === 'function'
    );
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isEditing) draftRef.current = rawValue;
    }, [isEditing, rawValue]);

    useEffect(() => {
        if (!isEditing) return undefined;

        const content = contentRef.current;
        const ownerWindow = content?.ownerDocument?.defaultView;
        if (!content || !ownerWindow) return undefined;

        const frameId = ownerWindow.requestAnimationFrame(() => {
            content.focus();
            const range = getRangeAtTextOffset(content, caretOffsetRef.current);
            caretOffsetRef.current = null;
            const selection = ownerWindow.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
        });
        return () => ownerWindow.cancelAnimationFrame(frameId);
    }, [isEditing]);

    const cancelEdit = () => {
        if (isSaving) return;
        draftRef.current = rawValue;
        setIsEditing(false);
    };

    const saveEdit = useCallback(
        async ({ closeImmediately = false } = {}) => {
            if (!resolvedFieldKey || isSaving) return false;

            setIsSaving(true);
            try {
                const saveRequest = inlineEdit.onInlineSave({
                    fieldKey: resolvedFieldKey,
                    value: draftRef.current,
                });
                if (closeImmediately) setIsEditing(false);

                const saved = await saveRequest;
                if (saved) setIsEditing(false);
                else if (closeImmediately) setIsEditing(true);
                return Boolean(saved);
            } finally {
                setIsSaving(false);
            }
        },
        [inlineEdit, isSaving, resolvedFieldKey]
    );

    useEffect(() => {
        if (!isEditing || isSaving) return undefined;

        const ownerDocument = wrapperRef.current?.ownerDocument;
        if (!ownerDocument) return undefined;

        const handleOutsidePointerDown = (event) => {
            if (wrapperRef.current?.contains(event.target)) return;
            if (draftRef.current === rawValue) {
                setIsEditing(false);
                return;
            }
            saveEdit({ closeImmediately: true });
        };

        ownerDocument.addEventListener(
            'pointerdown',
            handleOutsidePointerDown,
            true
        );
        return () =>
            ownerDocument.removeEventListener(
                'pointerdown',
                handleOutsidePointerDown,
                true
            );
    }, [isEditing, isSaving, rawValue, saveEdit]);

    return (
        <span
            ref={wrapperRef}
            data-inline-editable-text={canInlineEdit ? 'true' : undefined}
            className={clsx(
                className,
                canInlineEdit && 'group/inline-button-edit relative cursor-text'
            )}
            onClick={(event) => {
                if (!canInlineEdit) return;

                event.preventDefault();
                event.stopPropagation();
                if (isEditing) return;

                caretOffsetRef.current = getTextOffsetFromPoint(
                    wrapperRef.current,
                    event.clientX,
                    event.clientY
                );
                draftRef.current = rawValue;
                setIsEditing(true);
            }}
        >
            {canInlineEdit && (
                <span
                    aria-hidden="true"
                    className={clsx(
                        'border-primary-500/70 pointer-events-none absolute -inset-x-1 -inset-y-0.5 z-0 rounded border-2 border-dashed transition-opacity',
                        isEditing
                            ? 'bg-white/[0.03] opacity-100'
                            : 'opacity-0 group-hover/inline-button-edit:opacity-100'
                    )}
                />
            )}
            {isEditing ? (
                <span
                    ref={contentRef}
                    contentEditable
                    suppressContentEditableWarning
                    role="textbox"
                    aria-label="Edit button text"
                    className="relative z-10 min-w-[1ch] outline-none"
                    onInput={(event) => {
                        draftRef.current =
                            event.currentTarget.textContent || '';
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            saveEdit({ closeImmediately: true });
                        }
                        if (event.key === 'Escape') {
                            event.preventDefault();
                            cancelEdit();
                        }
                    }}
                >
                    {rawValue}
                </span>
            ) : (
                children
            )}
        </span>
    );
}
